import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import {
  FileText, Upload, Trash2, CheckCircle, AlertTriangle, AlertCircle, Calendar,
  Plus, Download, X, ShieldCheck, ShieldAlert, IdCard,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPES = [
  { id: 'cdl', label: 'CDL', icon: IdCard },
  { id: 'medical_card', label: 'Medical Card', icon: ShieldAlert },
  { id: 'insurance_certificate', label: 'Insurance Cert', icon: ShieldCheck },
  { id: 'drug_test', label: 'Drug Test', icon: FileText },
  { id: 'mvr', label: 'MVR', icon: FileText },
  { id: 'twic', label: 'TWIC', icon: IdCard },
  { id: 'hazmat', label: 'HazMat Endorsement', icon: ShieldAlert },
  { id: 'w9', label: 'W-9', icon: FileText },
  { id: 'other', label: 'Other', icon: FileText },
];
const DOC_TYPE_LABEL = Object.fromEntries(DOC_TYPES.map(d => [d.id, d.label]));

const STATUS = {
  ok: { label: 'Valid', cls: 'bg-green-100 text-green-700', icon: CheckCircle },
  expiring_soon: { label: 'Expiring Soon', cls: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  expired: { label: 'Expired', cls: 'bg-red-100 text-red-700', icon: AlertCircle },
};

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return iso || '—'; } };

const MyDocuments = () => {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [form, setForm] = useState({ doc_type: 'cdl', expires_at: '', issued_at: '', notes: '', file: null });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/driver-documents`, { withCredentials: true });
      setDocs(res.data);
    } finally { setLoading(false); }
  };

  const showToast = (kind, msg) => { setToast({ kind, msg }); setTimeout(() => setToast(null), 3500); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.file) { showToast('error', 'Please select a file'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('driver_id', user.id);
      fd.append('doc_type', form.doc_type);
      if (form.expires_at) fd.append('expires_at', form.expires_at);
      if (form.issued_at) fd.append('issued_at', form.issued_at);
      if (form.notes) fd.append('notes', form.notes);
      await axios.post(`${API}/driver-documents`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      showToast('success', 'Document uploaded');
      setForm({ doc_type: 'cdl', expires_at: '', issued_at: '', notes: '', file: null });
      if (fileRef.current) fileRef.current.value = '';
      setShowForm(false);
      await fetchAll();
    } catch (err) {
      showToast('error', err.response?.data?.detail || 'Upload failed');
    } finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try { await axios.delete(`${API}/driver-documents/${id}`, { withCredentials: true }); showToast('success', 'Deleted'); await fetchAll(); }
    catch (err) { showToast('error', err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <DashboardLayout><div className="p-8 text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loading...</div></DashboardLayout>;

  const expired = docs.filter(d => d.status === 'expired').length;
  const expiring = docs.filter(d => d.status === 'expiring_soon').length;

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-3xl mx-auto" data-testid="my-documents-page">
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>My Documents</h1>
            <p className="text-base text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Keep your CDL, medical card, insurance, and DOT paperwork current.</p>
          </div>
          <button data-testid="my-upload-button" onClick={() => setShowForm(true)} className="bg-[#002FA7] text-white px-4 py-2.5 rounded-md font-semibold hover:bg-[#00247D] transition-all flex items-center gap-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <Plus className="w-4 h-4" /> Upload Document
          </button>
        </div>

        {toast && (
          <div data-testid="my-docs-toast" className={`mb-4 px-4 py-3 rounded-md text-sm flex items-center gap-2 border ${toast.kind === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {toast.kind === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />} {toast.msg}
          </div>
        )}

        {(expired + expiring) > 0 && (
          <div data-testid="my-compliance-warning" className="mb-6 bg-amber-50 border border-amber-200 rounded-md p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                {expired > 0 && `${expired} expired`}
                {expired > 0 && expiring > 0 && ' · '}
                {expiring > 0 && `${expiring} expiring within 30 days`}
              </p>
              <p className="text-xs text-amber-800 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Renew or replace these documents to stay road-legal.</p>
            </div>
          </div>
        )}

        {docs.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center" data-testid="no-my-docs">
            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>You haven't uploaded any documents yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {docs.map(d => {
              const status = STATUS[d.status] || STATUS.ok;
              const Icon = status.icon;
              const TypeIcon = (DOC_TYPES.find(t => t.id === d.doc_type)?.icon) || FileText;
              return (
                <div key={d.id} data-testid={`my-doc-${d.id}`} className="bg-white border border-slate-200 rounded-md p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 ${d.status === 'expired' ? 'bg-red-100' : d.status === 'expiring_soon' ? 'bg-amber-100' : 'bg-[#002FA7]/10'}`}>
                      <TypeIcon className={`w-5 h-5 ${d.status === 'expired' ? 'text-red-700' : d.status === 'expiring_soon' ? 'text-amber-700' : 'text-[#002FA7]'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{DOC_TYPE_LABEL[d.doc_type] || d.doc_type}</p>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${status.cls}`}>
                          <Icon className="w-3 h-3" /> {status.label}
                        </span>
                      </div>
                      <div className="text-xs text-slate-600 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        {d.issued_at && <span><Calendar className="w-3 h-3 inline mr-0.5" />Issued {fmtDate(d.issued_at)}</span>}
                        {d.expires_at && <span>Expires {fmtDate(d.expires_at)}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button data-testid={`my-download-${d.id}`} onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${d.file_url}`, '_blank')} className="p-2 rounded-md text-slate-700 hover:bg-slate-100"><Download className="w-4 h-4" /></button>
                    <button data-testid={`my-delete-${d.id}`} onClick={() => remove(d.id)} className="p-2 rounded-md text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4" data-testid="my-doc-modal">
          <div className="bg-white rounded-md w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Upload Document</h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Document Type *</label>
                <select data-testid="my-doc-type-select" required value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm">
                  {DOC_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>File * (PDF / JPG / PNG, max 10MB)</label>
                <input data-testid="my-doc-file-input" ref={fileRef} required type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} className="w-full text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Issued Date</label>
                  <input type="date" value={form.issued_at} onChange={(e) => setForm({ ...form, issued_at: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Expires</label>
                  <input data-testid="my-doc-expires-input" type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Notes</label>
                <textarea rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
              </div>
              <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-100" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Cancel</button>
                <button data-testid="my-submit-doc" type="submit" disabled={submitting} className="px-5 py-2 bg-[#002FA7] text-white rounded-md text-sm font-semibold hover:bg-[#00247D] transition-all disabled:opacity-50 flex items-center gap-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <Upload className="w-4 h-4" /> {submitting ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default MyDocuments;
